import { getDenoiserExtension } from '../agoraLoader';
import loggerService from '../logger.service';

import type { DenoiserHook } from './types';

/**
 * v2 denoiser hook (agora-extension-ai-denoiser ^2.0.2).
 *
 * Key differences from v1:
 * - `pipeerror` event on the processor replaces `extension.onloaderror`.
 * - `overload` event on the processor replaces `processor.onoverload`.
 * - `processor.destroy()` is available for proper teardown.
 * - `processor.setLatency('LOW' | 'FULL')` is available (returns Promise).
 *
 * Event-emitter methods (on/off/once/removeAllListeners) live on the processor,
 * NOT on the extension.
 */

async function detachFromPipeline(processor: any): Promise<void> {
  try {
    await processor.disable();
  } catch (e) {
    loggerService.warn('[DenoiserV2] processor.disable() during detach:', e);
  }
  try {
    await processor.unpipe();
  } catch (e) {
    loggerService.warn('[DenoiserV2] processor.unpipe() during detach:', e);
  }
}

export default function useDenoiserV2(): DenoiserHook {
  return {
    createProcessor() {
      const denoiser = getDenoiserExtension();
      if (!denoiser) {
        loggerService.error('[DenoiserV2] Extension not available when creating processor');
        return null;
      }

      const processor = denoiser.createProcessor();

      // pipeerror replaces extension.onloaderror in v1
      processor.on('pipeerror', (error: Error) => {
        loggerService.error('[DenoiserV2] pipeerror:', error);
      });

      return processor;
    },

    async configureAfterPipe(processor: any) {
      // setLatency returns Promise<void> in v2.0.2; await to surface rejections.
      try {
        await processor.setLatency('LOW');
      } catch (e) {
        loggerService.warn('[DenoiserV2] setLatency failed:', e);
      }
    },

    setupOverloadHandler(processor: any, onOverload: (elapsedMs?: number) => void) {
      processor.removeAllListeners('overload');
      // v2's EventEmitter overload callback takes no parameters (per the SDK's
      // AIDenoiserProcessorOverloadCallback type). Only the deprecated
      // assignment-style `onoverload` receives elapsedTime.
      processor.on('overload', () => onOverload(undefined));
    },

    detachProcessor(processor: any) {
      return detachFromPipeline(processor);
    },

    async destroyProcessor(processor: any) {
      await detachFromPipeline(processor);
      processor.removeAllListeners('pipeerror');
      processor.removeAllListeners('overload');
      processor.removeAllListeners('dump');
      processor.removeAllListeners('dumpend');
      try {
        await processor.destroy();
      } catch (e) {
        loggerService.error('[DenoiserV2] Error during processor.destroy():', e);
      }
    },

    // Can rebuild returns true since v2's destroy() allows safe recreation without WASM leaks.
    canRebuildProcessor() {
      return true;
    },

    checkCompatibility() {
      const denoiser = getDenoiserExtension();
      if (!denoiser) {
        // Optimistic: `isDeNoiserCompatibleSync` (stream.service.ts) relies on
        // this returning `true` so the UI doesn't hide noise-cancellation during
        // the brief async SDK load. Real compatibility is re-checked once the
        // SDK resolves (see `isDeNoiserCompatible`).
        return true;
      }
      return denoiser.checkCompatibility();
    },

    getName() {
      return 'v2';
    }
  };
}
